// 雲錄來源講一次就懂（VMS UX 評審第二輪 R2-P1-7、R2-P1-1）：席次之外，哪些攝影機的雲錄是家戶方案（或合約）提供的。
// 帳單 #seats 下方、總覽「雲錄席次」卡同一句：「另外由家戶方案提供雲錄的攝影機：3KPT（lab · 免費方案）」。
// 資料是每台的錄影政策（façade recording-policy 的 cloud.source = home_plan，跟攝影機錄影與儲存分頁同一份、同一個快取 key）；
// 每台一個請求，最多查 HOME_PLAN_CHECK_MAX 台（同總覽 24 小時缺口的做法）。
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { vms } from '@kinio/web-sdk';
import type { VmsDevice } from '../../lib/devices';

export const HOME_PLAN_CHECK_MAX = 200; // 總覽與帳單要數到同一個數（第四輪 B4-09：48 台上限讓兩頁各查到不同的前 48 台）

export interface HomePlanCam {
  d: VmsDevice;
  /** 方案名（source_label）；沒帶 = '' */
  plan: string;
}

interface CloudSrc {
  cloud?: { effective?: boolean; source?: string | null; source_label?: string | null } | null;
}

/** 錄影政策回應 → 是不是由家戶方案提供雲錄（生效中且 source = home_plan）。 */
export function homePlanOf(p: CloudSrc | undefined): { plan: string } | null {
  const c = p?.cloud;
  if (!c || c.source !== 'home_plan' || c.effective === false) return null;
  return { plan: c.source_label ?? '' };
}

export function useHomePlanCloud(cams: VmsDevice[], enabled = true): { list: HomePlanCam[]; loading: boolean } {
  const checked = useMemo(() => cams.slice(0, HOME_PLAN_CHECK_MAX), [cams]);
  const qs = useQueries({
    queries: checked.map((d) => ({
      queryKey: ['rec-policy', d.homeId, d.aid],
      queryFn: () => vms.recordingPolicy(d.apiEndpoint, d.homeId, d.aid) as Promise<CloudSrc>,
      enabled,
      staleTime: 60_000,
      retry: false,
    })),
  });
  const list: HomePlanCam[] = [];
  checked.forEach((d, i) => {
    const hp = homePlanOf(qs[i]?.data);
    if (hp) list.push({ d, plan: hp.plan });
  });
  return { list, loading: enabled && qs.some((q) => q.isLoading) };
}

type T = (k: string, o?: Record<string, unknown>) => string;

/** 一句話：「另外由家戶方案提供雲錄的攝影機：3KPT（lab · 免費方案）、客廳（我的家）等 N 台」。沒有 = ''。 */
export function homePlanSentence(t: T, list: HomePlanCam[], max = 3, nameOf: (d: VmsDevice) => string = (d) => d.name): string {
  if (!list.length) return '';
  // 名稱用完整名（通道寫「lstw-16ch / CH2」，第三輪 B3-21）
  const names = list
    .slice(0, max)
    .map(({ d, plan }) => t('billing.home_plan_item', { name: nameOf(d), where: [d.homeName, plan].filter(Boolean).join(' · ') }))
    .join(t('common.list_sep'));
  const more = list.length > max ? t('overview.and_more', { count: list.length - max }) : '';
  return t('billing.home_plan_cams', { names: names + more, count: list.length });
}
